import { Filter } from '@flexible-reports/store/api/types';
import { ScheduledReportStatus } from '@flexible-reports/store/api/reports/types';

export interface FlexibleSubscription {
  readonly id: string;
  readonly state: string;
  readonly name: string;
  readonly reportId: string | null;
  readonly status: string;
  readonly failureCode: string | null;
  readonly lastRun: string | null;
  readonly nextRun: string | null;
  readonly accounts: string[];
  readonly advertisers: string[];
  readonly fields: string[];
  readonly frequency: string;
  readonly reportPeriod: {
    readonly datePeriod?: {
      readonly startDate: string;
      readonly endDate: string;
    };
    readonly preset?: string;
  };
  readonly format: string;
  readonly lastModifiedDate: string | null;
  readonly createdDate: string | null;
  readonly scheduleEndDate: string | null;
  readonly recipients: string[];
  // For Multi-account Authorization
  readonly linkedAccounts: AccountId[];
  readonly templateId?: string;
}

// Ads Domain models
// https://domain-model.tada.amazon.dev/tree?entity=ReportConfiguration
export interface ReportConfiguration {
  readonly reportConfigurationId: string;
  readonly name: string;
  readonly status: ReportConfigurationStatus;
  readonly linkedAccounts: AccountId[];
  readonly linkedQuery: LinkedQuery;
  readonly period: ReportPeriod;
  readonly schedule: ReportConfigurationSchedule;
  readonly scheduleType: ScheduleType;
  readonly deliverySettings: ReportDeliverySettings;
  readonly locale: string;
  readonly format: ReportFormat;
  readonly latestScheduledReportId: string | null;
  readonly latestScheduledReportStatus: ScheduledReportStatus;
  readonly latestScheduledReportFailureCode: string | null;
  readonly latestScheduledReportLastUpdatedDateTime: string | null;
  readonly creationDateTime: string | null;
  readonly lastUpdatedDateTime: string | null;
  readonly nextScheduledReportDateTime: string | null;
  readonly currencyOfView: string | null;
  readonly timeZoneMode?: string;
}

export enum ReportConfigurationStatus {
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
  EXPIRED = 'EXPIRED'
}
export type ReportConfigurationStatusType = keyof typeof ReportConfigurationStatus;

export interface AccountId {
  readonly managerAccountId?: string;
  readonly advertiserAccountId?: string;
}

export interface AccessRequestedAccountId {
  readonly managerAccountId?: string;
  readonly advertiserAccountId?: string;
}

export enum ReportSource {
  REPORTING = 'REPORTING',
  AMC = 'AMC'
}

export const REPORT_SOURCE_TRANSLATION_KEYS: Record<ReportSource, { id: string; fallback: string }> = {
  [ReportSource.REPORTING]: { id: 'flexible_reports_report_source_reporting', fallback: 'Reporting' },
  [ReportSource.AMC]: { id: 'flexible_reports_report_source_amc', fallback: 'AMC' }
};

export interface LinkedQuery {
  readonly reportingQuery?: ReportingQuery;
  readonly templateQuery?: TemplateQuery;
}

export interface ReportingQuery {
  readonly fields: string[];
  readonly filter?: Filter;
}

export interface TemplateQuery {
  readonly templateId: string;
  readonly parameters: TemplateParameter[];
}

export interface TemplateParameter {
  id: string;
  type: string;
  value: ParameterValue;
}

export interface ParameterValue {
  readonly stringValue?: StringValue;
  readonly stringArray?: StringArray;
}

export interface StringValue {
  readonly value: string;
}

export interface StringArray {
  readonly value: string[];
}

export interface ReportPeriod {
  readonly datePeriod?: DatePeriod;
  readonly reportConfigurationPresetPeriod?: ReportPresetPeriod;
}

export interface DatePeriod {
  readonly startDate: string;
  readonly endDate: string;
}

export interface ReportPresetPeriod {
  readonly preset: PeriodPreset;
}

export interface ReportConfigurationSchedule {
  readonly nowSchedule?: NowSchedule;
  readonly futureSchedule?: FutureSchedule;
  readonly recurringMonthlySchedule?: RecurringMonthlySchedule;
  readonly recurringWeeklySchedule?: RecurringWeeklySchedule;
  readonly recurringDailySchedule?: RecurringSchedule;
  readonly recurringMonthlyPresetSchedule?: RecurringMonthlyPresetSchedule;
}

export interface NowSchedule {
  readonly timeZone: string;
}

export interface FutureSchedule {
  readonly hourOfDay: number;
  readonly timeZone: string;
  readonly runDate: string;
}

export interface RecurringMonthlySchedule extends RecurringSchedule {
  readonly dayOfMonth: number;
}

export interface RecurringMonthlyPresetSchedule extends RecurringSchedule {
  readonly preset: string;
}

export interface RecurringWeeklySchedule extends RecurringSchedule {
  readonly daysOfWeek: string[];
}

export interface RecurringSchedule {
  readonly hourOfDay: number;
  readonly timeZone: string;
  readonly expirationDate: string;
}

export enum ScheduleType {
  NOW = 'NOW',
  FUTURE = 'FUTURE',
  RECURRING_DAILY = 'RECURRING_DAILY',
  RECURRING_WEEKLY = 'RECURRING_WEEKLY',
  RECURRING_MONTHLY = 'RECURRING_MONTHLY'
}

export enum PeriodPreset {
  TODAY = 'TODAY',
  YESTERDAY = 'YESTERDAY',
  WEEK_TO_DATE = 'WEEK_TO_DATE',
  MONTH_TO_DATE = 'MONTH_TO_DATE',
  LAST_WEEK = 'LAST_WEEK',
  LAST_MONTH = 'LAST_MONTH',
  LAST_7_DAYS = 'LAST_7_DAYS',
  LAST_14_DAYS = 'LAST_14_DAYS',
  LAST_30_DAYS = 'LAST_30_DAYS',
  LAST_90_DAYS = 'LAST_90_DAYS',
  QUARTER_TO_DATE = 'QUARTER_TO_DATE',
  LAST_QUARTER = 'LAST_QUARTER',
  YEAR_TO_DATE = 'YEAR_TO_DATE',
  LAST_YEAR = 'LAST_YEAR'
}

export enum ReportFormat {
  CSV = 'CSV',
  XLSX = 'XLSX'
}

export enum SortableField {
  STATE = 'status',
  NAME = 'name',
  STATUS = 'latestScheduledReportStatus',
  FREQUENCY = 'scheduleType',
  FORMAT = 'format',
  CREATION_DATE = 'creationDateTime',
  LAST_UPDATED_DATE = 'lastUpdatedDateTime',
  LAST_RUN_DATE = 'latestScheduledReportLastUpdatedDateTime',
  NEXT_RUN_DATE = 'nextScheduledReportDateTime'
}

export interface ReportDeliverySettings {
  readonly email: string[] | null;
  readonly s3?: string[];
}

export interface ReportConfigurationCreate {
  name: string;
  status: ReportConfigurationStatus;
  linkedAccounts: AccountId[];
  linkedQuery: LinkedQuery;
  period: ReportPeriod;
  schedule: ReportConfigurationSchedule;
  scheduleType: ScheduleType;
  deliverySettings?: ReportDeliverySettings;
  locale: string;
  format: ReportFormat;
  emailDeliveryMarketplace: string;
  currencyOfView?: string;
  timeZoneMode?: string;
}

export interface ReportConfigurationUpdate {
  reportConfigurationId: string;
  name?: string;
  status?: ReportConfigurationStatus;
  schedule?: ReportConfigurationSchedule;
  scheduleType?: ScheduleType;
  deliverySettings?: ReportDeliverySettings;
  period?: ReportPeriod;
  linkedAccounts?: AccountId[];
  linkedQuery?: LinkedQuery;
  currencyOfView?: string | null;
  timeZoneMode?: string;
}

export type ReportConfigurationStatusUpdate = {
  reportConfigurationId: string;
  status: ReportConfigurationStatus;
};

export interface ReportConfigurationMultiStatusSuccess {
  readonly index: number;
  readonly reportConfiguration: ReportConfiguration;
}
